import { type MetaFunction } from "react-router"
import {
	TypographyH1,
	TypographyH2,
	TypographyH3,
	TypographyP,
} from "#app/components/ui/typography.tsx"
import { APP_NAME } from "#app/utils/constants"
import { generateMeta } from "#app/utils/meta"

export const meta: MetaFunction = () => {
	return generateMeta({
		title: `Política de Privacidad - ${APP_NAME}`,
		description:
			"Política de privacidad de Greta's Patchwork. Información sobre cómo recopilamos, utilizamos y protegemos tus datos personales.",
		canonical: "/privacy",
	})
}

export default function PrivacyRoute() {
	return (
		<main className="container h-full py-12">
			<div className="mx-auto max-w-3xl">
				<TypographyH1>Política de Privacidad</TypographyH1>

				<TypographyP className="mt-6">
					En {APP_NAME}, nos comprometemos a proteger tu privacidad. Esta
					política describe cómo recopilamos, utilizamos y salvaguardamos tu
					información personal.
				</TypographyP>

				<TypographyH2 className="mt-8">
					1. Información que Recopilamos
				</TypographyH2>
				<TypographyP>
					Podemos recopilar los siguientes tipos de información:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						<strong>Información personal:</strong> nombre, dirección, email,
						teléfono, datos de pago
					</li>
					<li>
						<strong>Información de cuenta:</strong> nombre de usuario,
						contraseña cifrada
					</li>
					<li>
						<strong>Información de navegación:</strong> dirección IP, navegador,
						páginas visitadas, tiempo de permanencia
					</li>
					<li>
						<strong>Historial de pedidos:</strong> productos comprados,
						direcciones de envío guardadas
					</li>
				</ul>

				<TypographyH2 className="mt-8">
					2. Cómo Utilizamos tu Información
				</TypographyH2>
				<TypographyP>Utilizamos tu información para:</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>Procesar y entregar tus pedidos</li>
					<li>Comunicarte sobre el estado de tus compras</li>
					<li>Mejorar nuestra tienda y experiencia de usuario</li>
					<li>Personalizar recomendaciones de productos</li>
					<li>Enviar newsletters (solo con tu consentimiento)</li>
					<li>Responder a tus preguntas y consultas</li>
					<li>Cumplir con obligaciones legales</li>
				</ul>

				<TypographyH2 className="mt-8">
					3. Base Legal para el Tratamiento
				</TypographyH2>
				<TypographyP>
					Tratamos tus datos personales bajo las siguientes bases legales:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						<strong>Ejecución del contrato:</strong> para procesar tus pedidos y
						entregas
					</li>
					<li>
						<strong>Consentimiento:</strong> para comunicaciones comerciales y
						newsletter
					</li>
					<li>
						<strong>Interés legítimo:</strong> para mejorar nuestros servicios y
						prevenir fraudes
					</li>
					<li>
						<strong>Cumplimiento legal:</strong> para obligaciones fiscales y
						legales
					</li>
				</ul>

				<TypographyH2 className="mt-8">4. Compartición de Datos</TypographyH2>
				<TypographyP>
					No vendemos tus datos personales. Podemos compartirlos con:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						<strong>Proveedores de pago:</strong> para procesar transacciones
						seguras (Stripe)
					</li>
					<li>
						<strong>Servicios de envío:</strong> para entregar tus pedidos
					</li>
					<li>
						<strong>Proveedores de hosting:</strong> para mantener nuestro sitio
					</li>
					<li>
						<strong>Autoridades legales:</strong> cuando sea requerido por ley
					</li>
				</ul>

				<TypographyH2 className="mt-8">
					5. Cookies y Tecnologías Similares
				</TypographyH2>
				<TypographyP>
					Nuestro sitio utiliza cookies para mejorar tu experiencia. Tipos de
					cookies que utilizamos:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						<strong>Esenciales:</strong> necesarias para el funcionamiento del
						sitio (carrito, sesión)
					</li>
					<li>
						<strong>Analíticas:</strong> para entender cómo los visitantes usan
						nuestro sitio
					</li>
					<li>
						<strong>Funcionales:</strong> para recordar tus preferencias
					</li>
					<li>
						<strong>Marketing:</strong> para mostrarte anuncios relevantes
					</li>
				</ul>
				<TypographyP className="mt-4">
					Puedes gestionar tus preferencias de cookies a través de nuestro panel
					de cookies o la configuración de tu navegador.
				</TypographyP>

				<TypographyH2 className="mt-8">6. Seguridad de los Datos</TypographyH2>
				<TypographyP>
					Implementamos medidas de seguridad técnicas y organizativas para
					proteger tus datos:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>Cifrado SSL/TLS en todas las transmisiones</li>
					<li>Almacenamiento cifrado de datos sensibles</li>
					<li>Acceso restringido a información personal</li>
					<li>Auditorías regulares de seguridad</li>
				</ul>

				<TypographyH2 className="mt-8">7. Retención de Datos</TypographyH2>
				<TypographyP>
					Mantendremos tus datos personales durante el tiempo necesario para
					cumplir con los fines descritos en esta política:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>Datos de cuenta: mientras mantengas tu cuenta activa</li>
					<li>Datos de pedidos: 5 años para cumplir obligaciones fiscales</li>
					<li>Datos de marketing: hasta que solicites darte de baja</li>
					<li>Datos de navegación: 12 meses</li>
				</ul>

				<TypographyH2 className="mt-8">8. Tus Derechos</TypographyH2>
				<TypographyP>
					Tienes los siguientes derechos sobre tus datos:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						<strong>Acceso:</strong> puedes solicitar una copia de tus datos
						personales
					</li>
					<li>
						<strong>Rectificación:</strong> puedes corregir datos inexactos
					</li>
					<li>
						<strong>Supresión:</strong> puedes solicitar la eliminación de tus
						datos
					</li>
					<li>
						<strong>Limitación:</strong> puedes solicitar restringir el
						tratamiento
					</li>
					<li>
						<strong>Portabilidad:</strong> puedes solicitar tus datos en formato
						estructurado
					</li>
					<li>
						<strong>Oposición:</strong> puedes oponerte al tratamiento marketing
					</li>
				</ul>
				<TypographyP className="mt-4">
					Para ejercer estos derechos, contacta con nosotros en{" "}
					<strong>info@gretaspatchwork.com</strong>
				</TypographyP>

				<TypographyH2 className="mt-8">9. Menores de Edad</TypographyH2>
				<TypographyP>
					Nuestro sitio no está dirigido a menores de 16 años. No recopilamos
					deliberadamente información personal de menores. Si eres padre o tutor
					y crees que tu hijo nos ha proporcionado información personal,
					contáctanos para eliminarla.
				</TypographyP>

				<TypographyH2 className="mt-8">
					10. Transferencias Internacionales
				</TypographyH2>
				<TypographyP>
					Si realizas un pedido desde fuera de España, tus datos pueden ser
					transferidos a países fuera del Espacio Económico Europeo (EEE).
					Aseguramos que estas transferencias cuenten con adecuadas protecciones
					según las leyes de protección de datos aplicables.
				</TypographyP>

				<TypographyH2 className="mt-8">
					11. Cambios en esta Política
				</TypographyH2>
				<TypographyP>
					Podemos actualizar esta política ocasionalmente. Los cambios se
					publicarán en esta página con una fecha de actualización revisada. Te
					recomendamos revisar esta política periódicamente.
				</TypographyP>

				<TypographyH2 className="mt-8">12. Contacto</TypographyH2>
				<TypographyP>
					Para cualquier pregunta sobre esta política de privacidad o sobre tus
					datos, contáctanos:
				</TypographyP>
				<ul className="my-6 ml-6 list-disc space-y-2">
					<li>
						<strong>Email:</strong> info@gretaspatchwork.com
					</li>
					<li>
						<strong>Formulario:</strong> Disponible en nuestra página de
						contacto
					</li>
				</ul>

				<TypographyH2 className="mt-8">13. Autoridad de Control</TypographyH2>
				<TypographyP>
					Tienes derecho a presentar una reclamación ante la Agencia Española de
					Protección de Datos (AEPD) si consideras que tus derechos han sido
					vulnerados.
				</TypographyP>

				<TypographyP className="mt-8 text-sm text-muted-foreground">
					Última actualización: Febrero 2026
				</TypographyP>
			</div>
		</main>
	)
}
